
// admin-ui/src/api/client.ts
// ALL RIGHTS RESERVED
//
// FIXED 2026-02-01: Added generic get() method for DataTablePage

/**
 * Admin Dashboard API Client
 * Connects to ledger service reporting endpoints
 */

// ═══════════════════════════════════════════════════════
// CONFIG
// ═══════════════════════════════════════════════════════

import { API_CONFIG } from '../config/api';
import { getStoredFingerprint, getStoredEmployeeId, getCurrentEmployee, isDevMode } from '../utils/auth';

const API_BASE = API_CONFIG.ledger;

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

export interface PeriodSummary {
  period: {
    start: string;
    end: string;
    days: number;
  };
  transactions: {
    total: number;
    byType: {
      mint: number;
      burn: number;
      transfer: number;
      reversal: number;
    };
    byStatus: {
      completed: number;
      failed: number;
      cancelled: number;
      pending: number;
    };
  };
  volume: {
    gross: string;
    net: string;
    byType: {
      mint: string;
      burn: string;
      transfer: string;
    };
  };
  fees: {
    total: string;
    distribution: {
      cxxr: string;
      ccc: string;
      reserve: string;
    };
    byType: {
      mint: string;
      transfer: string;
      withdrawal: string;
    };
  };
}

export interface PerformanceMetrics {
  uptime: {
    percentMonth: number;
    totalMinutes: number;
    downtimeMinutes: number;
  };
  processing: {
    avgTimeMs: number;
    p50TimeMs: number;
    p95TimeMs: number;
    p99TimeMs: number;
  };
  throughput: {
    txPerSecond: number;
    txPerMinute: number;
    txPerHour: number;
  };
}

export interface ReserveStatus {
  currentReserve: string;
  totalSupply: string;
  ratio: number;
  compliant: boolean;
  lastVerified: string | null;
  anchorCount: number;
}

export interface AnchorChainStatus {
  chainLength: number;
  lastAnchor: {
    sequence: number;
    hash: string;
    timestamp: string;
    txCount: number;
  } | null;
  genesisTimestamp: string | null;
  verified: boolean;
  errors: string[];
}

export interface DailyVolume {
  date: string;
  txCount: number;
  volume: string;
  fees: string;
}

export interface DashboardData {
  report: 'dashboard';
  generatedAt: string;
  period: {
    start: string;
    end: string;
    monthName: string;
  };
  summary: PeriodSummary;
  performance: PerformanceMetrics;
  reserve: ReserveStatus;
  anchors: AnchorChainStatus;
  charts: {
    dailyVolume: DailyVolume[];
  };
}

export interface SystemStatus {
  status: 'NORMAL' | 'DELAYED' | 'BANK_DEGRADED' | 'CRITICAL';
  queueDepth: number;
  thresholds: {
    delayed: number;
    bankDegraded: number;
    critical: number;
  };
  disabledActions: string[];
  message: string;
  lastAnchor: string | null;
}

// ═══════════════════════════════════════════════════════
// API CLIENT
// ═══════════════════════════════════════════════════════

class ApiClient {
  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    // Auth: use fingerprint (production) or dev-employee headers (dev mode)
    const fingerprint = getStoredFingerprint();
    const employeeId = getStoredEmployeeId();

    if (fingerprint) {
      headers['x-client-cert-fingerprint'] = fingerprint;
    } else if (employeeId && isDevMode()) {
      headers['x-dev-employee-id'] = employeeId;
      const employee = getCurrentEmployee();
      if (employee) {
        headers['x-dev-employee-role'] = employee.roles[0] || 'admin';
        headers['x-dev-employee-roles'] = JSON.stringify(employee.roles);
      }
    }

    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // GENERIC HTTP METHODS (for DataTablePage and other dynamic endpoints)
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Generic GET request
   * Used by DataTablePage for fetching table data from dynamic endpoints
   */
  async get<T = any>(path: string): Promise<T> {
    return this.request<T>(path);
  }

  /**
   * Generic POST request
   */
  async post<T = any>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * Generic PUT request
   */
  async put<T = any>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * Generic DELETE request
   */
  async delete<T = any>(path: string): Promise<T> {
    return this.request<T>(path, {
      method: 'DELETE',
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // TYPED ENDPOINTS
  // ─────────────────────────────────────────────────────────────────────────

  // System status
  async getSystemStatus(): Promise<SystemStatus> {
    return this.request('/api/v1/system/status');
  }

  // Reports
  async getDashboard(): Promise<DashboardData> {
    return this.request('/api/v1/reports/dashboard');
  }

  async getSummary(start?: string, end?: string): Promise<PeriodSummary & { report: string; generatedAt: string }> {
    const params = new URLSearchParams();
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    const query = params.toString() ? `?${params}` : '';
    return this.request(`/api/v1/reports/summary${query}`);
  }

  async getMonthlyReport(year?: number, month?: number): Promise<{
    report: string;
    year: number;
    month: number;
    monthName: string;
    generatedAt: string;
    summary: PeriodSummary;
    performance: PerformanceMetrics;
  }> {
    const params = new URLSearchParams();
    if (year) params.set('year', String(year));
    if (month) params.set('month', String(month));
    const query = params.toString() ? `?${params}` : '';
    return this.request(`/api/v1/reports/monthly${query}`);
  }

  async getPerformance(start?: string, end?: string): Promise<{
    report: string;
    generatedAt: string;
    period: { start: string; end: string };
    metrics: PerformanceMetrics;
    compliance: {
      processingTime: { target: number; actual: number; compliant: boolean };
      uptime: { target: number; actual: number; compliant: boolean };
    };
  }> {
    const params = new URLSearchParams();
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    const query = params.toString() ? `?${params}` : '';
    return this.request(`/api/v1/reports/performance${query}`);
  }

  async getReserveStatus(): Promise<ReserveStatus & { report: string; generatedAt: string; target: { ratio: number; description: string } }> {
    return this.request('/api/v1/reports/reserve');
  }

  async getDailyVolume(start?: string, end?: string): Promise<{
    report: string;
    generatedAt: string;
    period: { start: string; end: string };
    data: DailyVolume[];
  }> {
    const params = new URLSearchParams();
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    const query = params.toString() ? `?${params}` : '';
    return this.request(`/api/v1/reports/volume${query}`);
  }

  async getAnchorStatus(): Promise<AnchorChainStatus & { report: string; generatedAt: string }> {
    return this.request('/api/v1/reports/anchors');
  }
}

export const api = new ApiClient();

